'use strict';

import { Vector2 } from 'three';
import { Grid } from './Grid';
import { Polygon } from '../../../../libs/Polygon';
import { BaseElement } from '../../../interfaces/BaseElement';
import { changeLangToCurrent } from '../../../components/langSwitcher/langSwitcher';


export class SchematicDraw {
	constructor () {
		this.width = 300;
		this.height = 300;
		this.container = new BaseElement()
			.addClass('schematic-draw')
			.DOMElement;
		
		this.canvasBlock = new BaseElement()
			.addClass('schematic-draw__canvas-block')
			.appendTo(this.container)
			.DOMElement;
		
		this.canvas = new BaseElement('canvas')
			.addClass('schematic-draw__canvas')
			.appendTo(this.canvasBlock)
			.DOMElement;
		
		this.canvas.width = this.width;
		this.canvas.height = this.height;
		this.color = null;
		this.active = false;
		
		const buttons = new BaseElement()
			.addClass('schematic-draw__buttons')
			.appendTo(this.container)
			.DOMElement;
		
		new BaseElement('button')
			.addClass('schematic-draw__clear-btn','schematic-draw__btn','language-string')
			.addAttribute('data-dictionary','Clear')
			.text(changeLangToCurrent('Clear'))
			.appendTo(buttons)
			.event('click',this.clear.bind(this));
		
		new BaseElement('button')
			.addClass('schematic-draw__apply-btn','schematic-draw__btn','language-string')
			.addAttribute('data-dictionary','Clear')
			.text(changeLangToCurrent('Apply'))
			.appendTo(buttons)
			.event('click',this.#apply.bind(this));
		
		this.model = null;
		this.cell = null;
		this.complete = false;
		this.pointsInterval = null;
		
		this.points = {
			ui: [],
			coords: [],
		};
		
		this.cellSize = 30;
		
		this.pointHelper = new BaseElement()
			.addClass('schematic-draw__point-helper')
			.appendTo(this.canvasBlock)
			.DOMElement;
		
		this.pointHelper.style.width = `${this.cellSize / 2}px`;
		this.pointHelper.style.height = `${this.cellSize / 2}px`;
		
		this.context = this.canvas.getContext('2d');
		this.grid = new Grid(this.context,this.width,this.height,this.cellSize,'#000000',1);
		this.canvasBlock.appendChild(this.grid.gridCanvas);
		this.grid.draw();
		
		this.coord = {
			x: 0,
			y: 0
		};
		
		this.drawHandler = (e) => this.#move(e);
		
		this.canvas.addEventListener('mousedown',this.#down.bind(this));
		this.canvas.addEventListener('mousemove',this.drawHandler);
		this.canvas.addEventListener('mouseleave',this.#mouseLeave.bind(this));
	}
	
	show(parent) {
		this.color = getComputedStyle(document.querySelector(':root')).getPropertyValue('--brand2');
		parent.appendChild(this.container);
		this.active = true;
	}
	
	hide() {
		this.container.remove();
		clearInterval(this.pointsInterval);
		this.complete = false;
		this.active = false;
	}
	
	#mouseLeave() {
		this.pointHelper.style.display = 'none';
	}
	
	getPoints() {
		return new Promise(resolve => {
			this.pointsInterval = setInterval(() => {
				if (this.complete) {
					clearInterval(this.pointsInterval);
					const points = [];
					this.points.coords.forEach(point => points.push(new Vector2(point[0],point[1] * -1)));
					
					const polyOut = new Polygon(points);
					polyOut.setLength(500);
					const polyIn = new Polygon(points);
					polyIn.setLength(500);
					polyIn.offset(-500 / 14,0,true);
					
					let contains = true;
					
					for (let i = 0; i < polyIn.curves.length; i++) {
						const curve = polyIn.curves[i];
						const v1 = polyOut.containsPoint(curve.v1) || polyOut.pointOnSegment(curve.v1);
						const v2 = polyOut.containsPoint(curve.v2) || polyOut.pointOnSegment(curve.v2);
						if (!v1 || !v2) {
							contains = false;
							break;
						}
					}
					
					if (!contains) {
						points.reverse();
					}
					
					resolve(points);
				}
			},500)
		});
	}
	
	#apply() {
		this.complete = true;
	}
	
	clear() {
		if (this.model && this.model.parent) {
			this.model.removeFromParent();
		}
		
		this.points.ui.forEach(el => el.remove());
		this.points.coords = [];
		this.context.clearRect(0,0,this.width,this.height);
		this.grid.draw();
	}
	
	#reposition (e) {
		const x = e.touches ? e.touches[0].clientX : e.clientX;
		const y = e.touches ? e.touches[0].clientY : e.clientY;
		this.bounding = this.canvas.getBoundingClientRect();
		this.coord.x = (x - this.bounding.left) / this.bounding.width  * this.width;
		this.coord.y = (y - this.bounding.top) / this.bounding.height * this.height;
	}
	
	#down(e) {
		this.#addPoint();
		this.#drawPath();
	}
	
	#move(e) {
		this.#reposition(e);
		this.#showPoint();
	}
	
	#addPoint() {
		if (this.cell) {
			const coords = this.cell.getClosestPoint(this.coord.x,this.coord.y,this.cellSize / 4);
			if (coords) {
				const point = this.pointHelper.cloneNode();
				point.style.left = coords.x - this.cellSize / 4 + 'px';
				point.style.top = coords.y - this.cellSize / 4 + 'px';
				point.style.display = 'block';
				this.canvasBlock.appendChild(point);
				this.points.ui.push(point);
				this.points.coords.push([coords.x,coords.y]);
			}
		}
	}
	
	#drawPath() {
		if (this.points.coords.length >= 2) {
			this.context.beginPath();
			this.context.lineWidth = 5;
			this.context.lineCap = "round";
			this.context.strokeStyle = this.color;
			const start = this.points.coords[0];
			this.context.moveTo(start[0], start[1]);
			
			for (let i = 1; i < this.points.coords.length; i++) {
				const point = this.points.coords[i];
				this.context.lineTo(point[0], point[1]);
			}
			
			this.context.stroke();
		}
	}
	
	#showPoint() {
		this.cell = this.grid.getCell(this.coord.x,this.coord.y);
		if (this.cell) {
			const coords = this.cell.getClosestPoint(this.coord.x,this.coord.y,this.cellSize / 4);
			if (coords) {
				this.pointHelper.style.left = coords.x - this.cellSize / 4 + 'px';
				this.pointHelper.style.top = coords.y - this.cellSize / 4 + 'px';
				this.pointHelper.style.display = 'block';
			} else {
				this.pointHelper.style.display = 'none';
			}
		} else {
			this.pointHelper.style.display = 'none';
		}
	}
	
}
